import type { DateRangeFilter } from 'types/graphql'

import type { HeroStatDelta } from './HeroStatCard'

type DateRangeBounds = { gte: Date; lte: Date }

/** Coerce GraphQL DateRangeFilter (ISO strings) or Date bounds for tooltip math. */
export const toDateRangeBounds = (
  dateRange?: DateRangeFilter | DateRangeBounds | null
): DateRangeBounds | undefined => {
  if (!dateRange?.gte || !dateRange?.lte) return undefined
  const gte =
    dateRange.gte instanceof Date ? dateRange.gte : new Date(dateRange.gte)
  const lte =
    dateRange.lte instanceof Date ? dateRange.lte : new Date(dateRange.lte)
  if (isNaN(gte.getTime()) || isNaN(lte.getTime())) return undefined
  return { gte, lte }
}

export const round1 = (n: number | null | undefined): number =>
  Math.round((n ?? 0) * 10) / 10

/**
 * Returns "Xpct%" for a numerator-of-denominator share, rounded to a whole
 * percent. Returns `undefined` when either value is missing or the
 * denominator is 0 so callers can choose how to render the missing state
 * (sub-line, tooltip, dash, etc.) rather than displaying "0%" or "NaN%".
 */
export const percentOf = (
  numerator: number | null | undefined,
  denominator: number | null | undefined
): string | undefined => {
  if (!numerator || !denominator) return undefined
  return `${Math.round((numerator / denominator) * 100)}%`
}

/** Comma-separated whole number for hero card values. Returns "0" when null. */
export const formatCount = (n: number | null | undefined): string =>
  n == null ? '0' : Math.round(n).toLocaleString()

/**
 * Formats a duration in seconds for hero card display.
 * - <1m: "Xs" (e.g. 47s)
 * - <1h: "Xm Ys" (e.g. 2m 31s) — seconds dropped if value is in minutes only
 * - ≥1h: "Xh Ym" (no seconds)
 */
export const formatDuration = (seconds: number | null | undefined): string => {
  if (seconds == null) return '—'
  const s = Math.max(0, Math.round(seconds))
  if (s < 60) return `${s}s`
  const hours = Math.floor(s / 3600)
  const minutes = Math.floor((s % 3600) / 60)
  const remSeconds = s % 60
  if (hours > 0) return `${hours}h ${minutes}m`
  if (remSeconds === 0) return `${minutes}m`
  return `${minutes}m ${remSeconds}s`
}

export type HeroStatUnit =
  | 'count'
  | 'percent'
  | 'days'
  | 'duration'
  | 'currency'

const currencyFormatter = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  maximumFractionDigits: 2,
})

export const formatPeriodValue = (
  v: number | null | undefined,
  unit: HeroStatUnit
): string => {
  if (v == null) return '—'
  if (unit === 'count') return Math.round(v).toLocaleString()
  if (unit === 'percent') return `${round1(v)}%`
  if (unit === 'duration') return formatDuration(v)
  if (unit === 'currency') return currencyFormatter.format(v)
  return `${round1(v)}d`
}

export interface DeltaInput {
  current: number | null | undefined
  previous: number | null | undefined
  format: 'percent-change' | 'pts' | 'days' | 'duration'
  /** True when a lower current value is the desirable outcome (e.g., Days to Hire). */
  lowerIsBetter?: boolean
  /** Tooltip prefixes — e.g. value units shown for "This period: X". */
  unit: HeroStatUnit
  /** Date range for the current period (used to calculate number of days) */
  currentDateRange?: DateRangeFilter | DateRangeBounds | null
}

export const buildDelta = ({
  current,
  previous,
  format,
  lowerIsBetter = false,
  unit,
  currentDateRange,
}: DeltaInput): HeroStatDelta | undefined => {
  if (current == null || previous == null) return undefined

  const rawChange = current - previous
  const isFlat = Math.abs(rawChange) < 0.05
  const valueWentUp = rawChange > 0

  const direction: HeroStatDelta['direction'] = isFlat
    ? 'neutral'
    : (lowerIsBetter ? !valueWentUp : valueWentUp)
    ? 'up'
    : 'down'

  let label: string
  let tooltipDelta: string

  if (format === 'percent-change') {
    if (previous === 0) return undefined
    const pct = (rawChange / previous) * 100
    label = `${round1(Math.abs(pct))}%`
    tooltipDelta = `${rawChange >= 0 ? '+' : ''}${Math.round(
      rawChange
    ).toLocaleString()} (${pct >= 0 ? '+' : '−'}${round1(Math.abs(pct))}%)`
  } else if (format === 'pts') {
    label = `${round1(Math.abs(rawChange))} pts`
    tooltipDelta = `${rawChange >= 0 ? '+' : '−'}${round1(
      Math.abs(rawChange)
    )} pts`
  } else if (format === 'duration') {
    // No "longer"/"shorter" qualifier — the up/down arrow in the badge
    // already conveys direction, and the extra word made the badge wide
    // enough to push the headline value down out of line with the other
    // hero cards.
    label = formatDuration(Math.abs(rawChange))
    tooltipDelta = `${rawChange >= 0 ? '+' : '−'}${formatDuration(
      Math.abs(rawChange)
    )}`
  } else {
    // days
    const qualifier = isFlat ? '' : valueWentUp ? ' slower' : ' faster'
    label = `${round1(Math.abs(rawChange))}d${qualifier}`
    tooltipDelta = `${rawChange >= 0 ? '+' : '−'}${round1(
      Math.abs(rawChange)
    )} days`
  }

  let tooltip: string
  const dateRangeBounds = toDateRangeBounds(currentDateRange)
  if (dateRangeBounds) {
    const dayMs = 24 * 60 * 60 * 1000
    const numDays = Math.round(
      (dateRangeBounds.lte.getTime() - dateRangeBounds.gte.getTime()) / dayMs
    )
    tooltip =
      `This period (${numDays} days): ${formatPeriodValue(current, unit)}\n` +
      `Prior period (${numDays} days): ${formatPeriodValue(previous, unit)}\n` +
      `Δ ${tooltipDelta}`
  } else {
    tooltip =
      `This period: ${formatPeriodValue(current, unit)}\n` +
      `Prior period: ${formatPeriodValue(previous, unit)}\n` +
      `Δ ${tooltipDelta}`
  }

  return { direction, label, tooltip }
}
